import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaTimes,
  FaShieldAlt,
  FaSave,
  FaCheck,
  FaList,
  FaEye,
  FaFileAlt,
  FaSearch,
  FaTimes as FaCancel,
  FaExchangeAlt
} from 'react-icons/fa';

import './PermissionsModal.css';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    font-size: 1.3rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    font-size: 1.2rem;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const ModalContent = styled.div`
  padding: 2rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const PermissionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const PermissionCard = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid ${props => props.checked ? '#0d6efd' : '#dee2e6'};
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #0d6efd;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
  }
  
  ${props => props.checked && `
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.1) 0%, rgba(66, 133, 244, 0.1) 100%);
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
  `}
`;

const PermissionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const PermissionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.checked ? 
    'linear-gradient(135deg, #0d6efd 0%, #4285f4 100%)' : 
    '#dee2e6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.checked ? 'white' : '#6c757d'};
  transition: all 0.3s ease;
  
  svg {
    font-size: 1rem;
  }
`;

const PermissionLabel = styled.div`
  flex: 1;
`;

const PermissionTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.checked ? '#0d6efd' : '#495057'};
`;

const PermissionDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #6c757d;
  line-height: 1.4;
`;

const CheckboxContainer = styled.div`
  position: relative;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const StyledCheckbox = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid ${props => props.checked ? '#0d6efd' : '#dee2e6'};
  border-radius: 6px;
  background: ${props => props.checked ? '#0d6efd' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  svg {
    color: white;
    font-size: 0.875rem;
    opacity: ${props => props.checked ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const ModalActions = styled.div`
  background: #f8f9fa;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-top: 1px solid #dee2e6;
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  
  svg {
    font-size: 1rem;
  }
  
  &.close-btn {
    background: white;
    color: #6c757d;
    border: 2px solid #dee2e6;
    
    &:hover {
      background: #f8f9fa;
      border-color: #adb5bd;
      color: #495057;
    }
  }
  
  &.save-btn {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    border: none;
    
    &:hover {
      background: linear-gradient(135deg, #218838 0%, #1ea97c 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
  }
`;

const permissionConfig = {
  all: {
    icon: FaShieldAlt,
    title: 'All Permissions',
    description: 'Grant complete access to all features'
  },
  inProgressList: {
    icon: FaList,
    title: 'In Progress List',
    description: 'View and manage in-progress items'
  },
  viewForm: {
    icon: FaFileAlt,
    title: 'View Form',
    description: 'Access to view form data'
  },
  viewRequest: {
    icon: FaEye,
    title: 'View Request',
    description: 'View request details and information'
  },
  goToInspection: {
    icon: FaSearch,
    title: 'Go To Inspection',
    description: 'Navigate to inspection pages'
  },
  cancelInspection: {
    icon: FaCancel,
    title: 'Cancel Inspection',
    description: 'Cancel scheduled inspections'
  },
  reassignRequest: {
    icon: FaExchangeAlt,
    title: 'Reassign Request',
    description: 'Reassign requests to other users'
  }
};

const PermissionsModal = ({ onClose, designationName }) => {

  const [permissions, setPermissions] = useState({
    all: false,
    inProgressList: false,
    viewForm: false,
    viewRequest: false,
    goToInspection: false,
    cancelInspection: false,
    reassignRequest: false
  });

  
  const handleCheckboxChange = (permission) => {
    setPermissions(prev => ({
      ...prev,
      [permission]: !prev[permission]
    }));
  };

  const formatLabel = (key) => {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FaShieldAlt />
            {designationName} Permissions
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <PermissionsGrid>
            {Object.entries(permissions).map(([key, value]) => {
              const config = permissionConfig[key];
              const IconComponent = config?.icon || FaShieldAlt;
              
              return (
                <PermissionCard
                  key={key}
                  checked={value}
                  onClick={() => handleCheckboxChange(key)}
                >
                  <PermissionHeader>
                    <PermissionIcon checked={value}>
                      <IconComponent />
                    </PermissionIcon>
                    <PermissionLabel>
                      <PermissionTitle checked={value}>
                        {config?.title || formatLabel(key)}
                      </PermissionTitle>
                      <PermissionDescription>
                        {config?.description || 'Permission access control'}
                      </PermissionDescription>
                    </PermissionLabel>
                    <CheckboxContainer>
                      <HiddenCheckbox
                        type="checkbox"
                        id={key}
                        checked={value}
                        onChange={() => handleCheckboxChange(key)}
                      />
                      <StyledCheckbox checked={value}>
                        <FaCheck />
                      </StyledCheckbox>
                    </CheckboxContainer>
                  </PermissionHeader>
                </PermissionCard>
              );
            })}
          </PermissionsGrid>
        </ModalContent>

        <ModalActions>
          <ActionButton className="close-btn" onClick={onClose}>
            <FaTimes />
            Close
          </ActionButton>
          <ActionButton className="save-btn">
            <FaSave />
            Save Changes
          </ActionButton>
        </ModalActions>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default PermissionsModal;